import { useState } from "react";
import { FileText, X } from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Textarea } from "./ui/textarea";
import api from "../lib/api";
import { toast } from "sonner";

export const ResumeUpload = ({ onClose, onUploadComplete }) => {
  const [saving, setSaving] = useState(false);
  const [resumeText, setResumeText] = useState("");

  const handleSave = async () => {
    const trimmedText = resumeText.trim();

    if (trimmedText.length < 100) {
      toast.error("Please paste at least 100 characters of your resume");
      return;
    }

    setSaving(true);

    try {
      // Sanitize text: remove null bytes and other problematic characters
      const sanitizedText = trimmedText
        .replace(/\u0000/g, "") // Remove null bytes
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "") // Remove other control characters
        .trim();

      if (sanitizedText.length < 100) {
        toast.error(
          "After cleaning, the text is too short. Please paste at least 100 characters."
        );
        return;
      }

      // Parse education section from resume text
      const parseEducation = (text) => {
        const educationRegex =
          /(?:EDUCATION|Education)([\s\S]*?)(?=\n(?:EXPERIENCE|Experience|SKILLS|Skills|$))/i;
        const match = text.match(educationRegex);

        if (match && match[1]) {
          const educationText = match[1].trim();
          return {
            raw: educationText,
            extracted: true,
          };
        }

        return {
          raw: "Not found in resume",
          extracted: false,
        };
      };

      const education = parseEducation(sanitizedText);

      // Create resume record via API
      await api.createResume({
        file_name: `resume_${Date.now()}.txt`,
        parsed_data: {
          fullText: sanitizedText,
          education: education,
        },
      });

      toast.success(
        `Resume saved successfully! (${sanitizedText.length} characters)`
      );
      onUploadComplete();
      onClose();
    } catch (error) {
      console.error("Error saving resume:", error);
      toast.error("Failed to save resume");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="flex flex-row items-start md:items-center justify-between gap-2 p-4 md:p-6">
        <div className="flex-1 min-w-0">
          <CardTitle className="text-lg md:text-xl">Add Your Resume</CardTitle>
          <CardDescription className="text-xs md:text-sm mt-1">
            Copy and paste your resume text to get personalized career coaching
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="flex-shrink-0">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 md:space-y-4 p-4 md:p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <FileText className="h-4 w-4 flex-shrink-0" />
          <p className="text-xs md:text-sm">
            Paste your resume text below (minimum 100 characters)
          </p>
        </div>

        <Textarea
          placeholder="Paste your resume text here..."
          value={resumeText}
          onChange={(e) => setResumeText(e.target.value)}
          className="min-h-[250px] md:min-h-[300px] font-mono text-xs md:text-sm"
          disabled={saving}
        />

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <p className="text-xs md:text-sm text-muted-foreground">
            {resumeText.length} characters
          </p>
          <Button
            onClick={handleSave}
            disabled={saving || resumeText.trim().length < 100}
            className="w-full sm:w-auto text-sm"
          >
            {saving ? "Saving..." : "Save Resume"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
